import AddIcon from "@mui/icons-material/Add";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import RemoveIcon from "@mui/icons-material/Remove";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

interface PanZoomViewportProps {
  children: React.ReactNode;
  contentWidth: number;
  contentHeight: number;
  height?: number | string;
  minScale?: number;
  maxScale?: number;
  fitToContent?: boolean;
  topLeftOverlay?: React.ReactNode;
  topRightOverlay?: React.ReactNode;
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const PanZoomViewport: React.FC<PanZoomViewportProps> = ({
  children,
  contentWidth,
  contentHeight,
  height = 320,
  minScale = 0.4,
  maxScale = 2.5,
  fitToContent = false,
  topLeftOverlay,
  topRightOverlay,
}) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const rightOverlayRef = useRef<HTMLDivElement>(null);
  const [leftOverlayMaxWidth, setLeftOverlayMaxWidth] = useState<number | undefined>();
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });

  const computeFitScale = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport || contentWidth <= 0 || contentHeight <= 0) {
      return 1;
    }

    const horizontalPadding = 32;
    const verticalPadding = 32;
    const topOverlayReserve = topLeftOverlay || topRightOverlay ? 56 : 0;
    const bottomControlsReserve = 48;

    const availableWidth = Math.max(viewport.clientWidth - horizontalPadding, 1);
    const availableHeight = Math.max(
      viewport.clientHeight - verticalPadding - topOverlayReserve - bottomControlsReserve,
      1
    );

    const fitScale = Math.min(availableWidth / contentWidth, availableHeight / contentHeight);
    return clamp(fitScale, minScale, maxScale);
  }, [contentHeight, contentWidth, maxScale, minScale, topLeftOverlay, topRightOverlay]);

  const resetView = useCallback(() => {
    setScale(fitToContent ? computeFitScale() : 1);
    setOffset({ x: 0, y: 0 });
  }, [computeFitScale, fitToContent]);

  useLayoutEffect(() => {
    if (!fitToContent) {
      return;
    }

    setScale(computeFitScale());
    setOffset({ x: 0, y: 0 });
  }, [computeFitScale, contentHeight, contentWidth, fitToContent]);

  useEffect(() => {
    if (!fitToContent) {
      return;
    }

    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    const handleResize = () => {
      setScale(computeFitScale());
      setOffset({ x: 0, y: 0 });
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(viewport);
    return () => resizeObserver.disconnect();
  }, [computeFitScale, fitToContent]);

  const zoomBy = useCallback(
    (delta: number) => {
      setScale((currentScale) => clamp(currentScale + delta, minScale, maxScale));
    },
    [maxScale, minScale]
  );

  useEffect(() => {
    const viewport = viewportRef.current;
    const rightOverlay = rightOverlayRef.current;

    if (!viewport || !topLeftOverlay || !topRightOverlay) {
      setLeftOverlayMaxWidth(undefined);
      return;
    }

    const updateLeftOverlayMaxWidth = () => {
      const viewportWidth = viewport.clientWidth;
      const rightWidth = rightOverlay?.offsetWidth ?? 0;
      const horizontalInset = 16;
      const gap = 12;
      setLeftOverlayMaxWidth(Math.max(180, viewportWidth - rightWidth - horizontalInset - gap));
    };

    updateLeftOverlayMaxWidth();

    const resizeObserver = new ResizeObserver(updateLeftOverlayMaxWidth);
    resizeObserver.observe(viewport);
    if (rightOverlay) {
      resizeObserver.observe(rightOverlay);
    }

    return () => resizeObserver.disconnect();
  }, [topLeftOverlay, topRightOverlay]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      const zoomDelta = event.deltaY < 0 ? 0.1 : -0.1;
      setScale((currentScale) => clamp(currentScale + zoomDelta, minScale, maxScale));
    };

    viewport.addEventListener("wheel", handleWheel, { passive: false });
    return () => viewport.removeEventListener("wheel", handleWheel);
  }, [maxScale, minScale]);

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return;
    }

    setIsPanning(true);
    panStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      offsetX: offset.x,
      offsetY: offset.y,
    };
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isPanning) {
      return;
    }

    const deltaX = event.clientX - panStartRef.current.x;
    const deltaY = event.clientY - panStartRef.current.y;
    setOffset({
      x: panStartRef.current.offsetX + deltaX,
      y: panStartRef.current.offsetY + deltaY,
    });
  };

  const endPan = () => setIsPanning(false);

  return (
    <Box
      ref={viewportRef}
      onMouseDown={handleMouseDown}
      onMouseLeave={endPan}
      onMouseMove={handleMouseMove}
      onMouseUp={endPan}
      sx={{
        bgcolor: "action.hover",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        cursor: isPanning ? "grabbing" : "grab",
        height,
        overflow: "hidden",
        position: "relative",
        userSelect: "none",
      }}
    >
      {/* Pannable content */}
      <Box
        sx={{
          height: contentHeight,
          left: "50%",
          position: "absolute",
          top: "50%",
          transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px)) scale(${scale})`,
          transformOrigin: "center center",
          width: contentWidth,
        }}
      >
        {children}
      </Box>

      {/* Top overlays */}
      {(topLeftOverlay || topRightOverlay) && (
        <Box
          onMouseDown={(e) => e.stopPropagation()}
          sx={{
            alignItems: "flex-start",
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            justifyContent: "space-between",
            left: 8,
            position: "absolute",
            right: 8,
            top: 8,
            zIndex: 2,
          }}
        >
          {topLeftOverlay && (
            <Box
              sx={{
                bgcolor: "background.paper",
                borderRadius: 1,
                boxShadow: 1,
                flex: "1 1 auto",
                maxWidth: leftOverlayMaxWidth ?? "100%",
                minWidth: 0,
              }}
            >
              {topLeftOverlay}
            </Box>
          )}
          {topRightOverlay && (
            <Box ref={rightOverlayRef} sx={{ flex: "0 0 auto", ml: topLeftOverlay ? 0 : "auto" }}>
              {topRightOverlay}
            </Box>
          )}
        </Box>
      )}

      {/* Bottom-right: zoom controls */}
      <Stack
        direction="row"
        spacing={0.25}
        onMouseDown={(e) => e.stopPropagation()}
        sx={{
          alignItems: "center",
          bgcolor: "background.paper",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          bottom: 8,
          boxShadow: 1,
          position: "absolute",
          px: 0.5,
          py: 0.25,
          right: 8,
          zIndex: 2,
        }}
      >
        <Tooltip title="Zoom out">
          <IconButton aria-label="Zoom out" onClick={() => zoomBy(-0.15)} size="small">
            <RemoveIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Typography color="text.secondary" sx={{ fontSize: "0.7rem", minWidth: 32, textAlign: "center" }}>
          {Math.round(scale * 100)}%
        </Typography>
        <Tooltip title="Zoom in">
          <IconButton aria-label="Zoom in" onClick={() => zoomBy(0.15)} size="small">
            <AddIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Reset view">
          <IconButton aria-label="Reset view" onClick={resetView} size="small">
            <CenterFocusStrongIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
};

export default PanZoomViewport;
